import { Injectable, inject, signal } from "@angular/core";
import { Observable, catchError, finalize, map, of, shareReplay, tap, timeout } from "rxjs";

import { ModuloEnMantenimiento } from "src/app/shared/model/mantenimiento-modulos/mantenimiento-modulos.model";
import { idEnSesion } from "src/app/vacaciones/frc-firmado/sesion";
import { MantenimientoModulosService } from "./mantenimiento-modulos.service";
import { coincide, rutasDe } from "./reglas";

/** Cuánto vale lo que se preguntó antes de volver a preguntar. */
export const VIGENCIA_MS = 60 * 1000;

/** Cuánto se espera al servidor; después se deja pasar (nunca se traba la navegación). */
export const ESPERA_MS = 5000;

/**
 * Lo que está en mantenimiento (16/10/2026), para el guardia de rutas,
 * Solicitudes y el diálogo de Tickets. Se pregunta al servidor como mucho
 * una vez por minuto y por persona. Si el servidor no contesta o falla, se
 * queda lo que había: un mantenimiento nunca puede tumbar el COS. En
 * mantenimiento no entra nadie, tampoco quien lo administra (Norman, al
 * probarlo): lo termina desde su pantalla, que nunca se bloquea.
 */
@Injectable({ providedIn: "root" })
export class EstadoMantenimientoService {
  private readonly api = inject(MantenimientoModulosService);

  private readonly lista = signal<ModuloEnMantenimiento[]>([]);
  private consultadoEn = 0;
  private consultadoPara: number | null = null;
  private enCurso: Observable<void> | null = null;

  /** Lo que está en mantenimiento ahora. */
  readonly activos = this.lista.asReadonly();

  /** Lo que haya; pregunta si ya pasó un minuto o si cambió la persona. Nunca falla. */
  asegurar(): Observable<void> {
    const yo = idEnSesion();
    if (yo === this.consultadoPara && Date.now() - this.consultadoEn < VIGENCIA_MS) {
      return of(undefined);
    }
    return this.refrescar();
  }

  /** Vuelve a preguntar ya. */
  refrescar(): Observable<void> {
    if (this.enCurso) {
      return this.enCurso;
    }
    const yo = idEnSesion();
    this.enCurso = this.api.activos().pipe(
      timeout(ESPERA_MS),
      tap((res) => {
        if (res && !res.codigoError) {
          this.lista.set(res.activos || []);
        }
      }),
      map(() => undefined),
      catchError(() => of(undefined)),
      finalize(() => {
        this.consultadoEn = Date.now();
        this.consultadoPara = yo;
        this.enCurso = null;
      }),
      shareReplay(1),
    );
    return this.enCurso;
  }

  /** Lo que devolvió la pantalla del módulo al guardar o terminar: así se ve ya, sin esperar el minuto. */
  pon(activos: ModuloEnMantenimiento[]): void {
    this.lista.set(activos || []);
    this.consultadoEn = Date.now();
    this.consultadoPara = idEnSesion();
  }

  /** El de esa clave, si está en mantenimiento. */
  porClave(clave: string): ModuloEnMantenimiento | undefined {
    return this.lista().find((m) => m.clave === clave);
  }

  /** El que bloquea esa URL, si alguno. */
  porRuta(url: string): ModuloEnMantenimiento | undefined {
    return this.lista().find((m) => coincide(url, rutasDe(m)));
  }

  /** Si le toca el aviso: lo que está en mantenimiento no lo abre nadie. */
  bloquea(modulo: ModuloEnMantenimiento | undefined): modulo is ModuloEnMantenimiento {
    return !!modulo;
  }
}
